// src/components/SearchSuggest.jsx
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchSearchCandidates } from '../utils/searchBooks';

function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// Витягуємо картинку з будь-якого з можливих полів
const getCover = (b) =>
  b.cover || b.coverUrl || b.imageUrl || b.thumbnail || b.photo || null;

export default function SearchSuggest({
  anchorRef,           // ref на контейнер пошуку (form/div)
  search,              // текст із інпуту
  onPick,              // клік по елементу
  onViewAll,           // "Переглянути всі"
  onClose,             // закрити поповер
  maxItems = 8,
  minPanelWidth = 0,
  panelClassName = '',
}) {
  const debounced = useDebounced((search || '').trim().toLowerCase(), 250);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState(null);
  const boxRef = useRef(null);
  const reqKeyRef = useRef(0);

  // Позиціонування під інпутом
  useEffect(() => {
    const updatePos = () => {
      const el = anchorRef?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + window.scrollY + 8, left: r.left + window.scrollX, width: r.width });
    };
    updatePos();
    const h = () => updatePos();
    window.addEventListener('resize', h);
    window.addEventListener('scroll', h, true);
    return () => {
      window.removeEventListener('resize', h);
      window.removeEventListener('scroll', h, true);
    };
  }, [anchorRef]);

  // Завантаження підказок: спочатку токени (будь-яке слово/префікс),
  // далі (на всякий) фолбек на префіксне orderBy title/author.
  useEffect(() => {
    let cancelled = false;
    const myKey = ++reqKeyRef.current;
    (async () => {
      const qText = (debounced || '').trim().toLowerCase();
      if (qText.length < 2) {
        setItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const take = maxItems + 1;
        let arr = await fetchSearchCandidates(qText, { take, minQueryLength: 2 });
        if (cancelled || reqKeyRef.current !== myKey) return;
        arr = Array.isArray(arr) ? arr.slice() : [];

        // легке сортування: початок назви → алфавіт
        arr.sort((a, b) => {
          const qa = (a.title || a.name || '').toLowerCase();
          const qb = (b.title || b.name || '').toLowerCase();
          const aStarts = qa.startsWith(qText) ? 0 : 1;
          const bStarts = qb.startsWith(qText) ? 0 : 1;
          if (aStarts !== bStarts) return aStarts - bStarts;
          return qa.localeCompare(qb);
        });

        if (!cancelled && reqKeyRef.current === myKey) { setItems(arr); setActive(0); }
      } catch (e) {
        if (!cancelled && reqKeyRef.current === myKey) setItems([]);
        console.warn('search suggest error:', e);
      } finally {
        if (!cancelled && reqKeyRef.current === myKey) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debounced, maxItems]);


  // Закриття + керування з клавіатури
  useEffect(() => {
    function onDocClick(e) {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target) && !anchorRef?.current?.contains(e.target)) onClose?.();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
      if (!items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, items.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
      if (e.key === 'Enter')     { e.preventDefault(); const it = items[active]; if (it) onPick?.(it); }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [items, active, onClose, onPick, anchorRef]);

  if (!rect || (!debounced && !loading)) return null;

  const showMore = items.length > maxItems;
  const visible = showMore ? items.slice(0, maxItems) : items;

  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const maxPanelWidth = Math.min(720, Math.max(280, viewportW - 16));
  const panelWidth = Math.min(Math.max(rect.width, minPanelWidth), maxPanelWidth);
  const minLeft = (typeof window !== 'undefined' ? window.scrollX : 0) + 8;
  const maxLeft = (typeof window !== 'undefined' ? window.scrollX : 0) + viewportW - panelWidth - 8;
  const panelLeft = Math.min(Math.max(rect.left, minLeft), Math.max(minLeft, maxLeft));

  const panel = (
    <div
      ref={boxRef}
      className={panelClassName}
      style={{
        position: 'absolute',
        top: rect.top,
        left: panelLeft,
        width: panelWidth,
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 12px 28px rgba(0,0,0,.18), 0 4px 8px rgba(0,0,0,.12)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        zIndex: 1000,
      }}
    >
      <div style={{ padding: '10px 14px', fontWeight: 700 }}>Книги</div>

      {loading && <div style={{ padding: 14, color: '#6b7280' }}>Шукаю…</div>}
      {!loading && !visible.length && <div style={{ padding: 14, color: '#6b7280' }}>Нічого не знайдено</div>}

      {!loading && visible.map((b, i) => (
        <div
          key={`${b.__col}-${b.id}`}
          onMouseEnter={() => setActive(i)}
          onClick={() => onPick?.(b)}
          style={{
            display: 'grid',
            gridTemplateColumns: '48px 1fr auto',
            gap: 12,
            padding: '10px 14px',
            background: i === active ? 'rgba(0,0,0,0.035)' : '#fff',
            cursor: 'pointer',
            borderTop: '1px solid #f3f4f6',
          }}
        >
          <div style={{ width: 48, height: 64, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden' }}>
            {getCover(b) && (
              <img src={getCover(b)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {b.title || b.name || 'Без назви'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {(b.author || b.authors?.join(', ') || '—')}{b.year ? ` • ${b.year}` : ''}{b.category ? ` • ${b.category}` : ''}
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>
            {b.location ? b.location : ''}
          </div>
        </div>
      ))}

      {!loading && (showMore || visible.length) && (
        <div
          onClick={() => onViewAll?.(debounced)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 14px',
            cursor: 'pointer',
            borderTop: '1px solid #f3f4f6',
            color: '#0b6c2b',
            fontWeight: 600,
          }}
        >
          <span>Переглянути всі результати</span>
          <span>→</span>
        </div>
      )}
    </div>
  );

  return createPortal(panel, document.body);
}
